import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ConstantActions } from '../../../Services/_actions/index.js';
import { callStatusListAtom } from '../../../Services/_states/Template/index.js';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../services/templateService';
import { templateReportingService } from '../services/templateReportingService';
import { TEMPLATE_USERS_PAGE_SIZE } from '../constants/templateApi';
import { formatReportingDate, USE_WHATSAPP_REPORTING } from '../constants/reportingConfig';
import { normalizeTemplate } from '../utils/normalizeTemplate';
import { templateFormAtom } from '../state/templateAtoms';

const DEFAULT_FILTERS = {
  templateId: '',
  sentDate: '',
  page: 0,
  search: '',
  status: '',
  assigned_to: '',
  sortField: 'total_users',
  sortOrder: 'desc',
  template_status: '',
  limit: TEMPLATE_USERS_PAGE_SIZE,
};

export default function useTemplateUsers() {
  const { templateId, templateSentDate, templateName } = useParams();
  const [searchParams] = useSearchParams();
  const botNumber = searchParams.get('bot') || '';
  const { showSnackBar } = useSnackBar();
  const constActions = ConstantActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const companyId = loggedInUser?.profile?.organisation?.organisation_id;
  const [recoilTemplate] = useRecoilState(templateFormAtom);
  const [callStatusList, setCallStatusList] = useState([]);
  const [, setGlobalCallStatusList] = useRecoilState(callStatusListAtom);

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [usersData, setUsersData] = useState({ users: [], total_count: 0 });
  const [template, setTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  const [modalShowCall, setModalShowCall] = useState(false);

  const fetchLegacyUsers = useCallback(
    async (nextFilters) => {
      const res = await templateService.getUserSummary(nextFilters);
      if (res?.status === 200) {
        setUsersData(res.data ?? { users: [], total_count: 0 });
      } else {
        setUsersData({ users: [], total_count: 0 });
      }
    },
    []
  );

  const fetchReportingUsers = useCallback(
    async (nextFilters) => {
      if (!companyId || !nextFilters.templateId) {
        setUsersData({ users: [], total_count: 0 });
        return;
      }

      const scope = {
        company_id: companyId,
        sent_date: formatReportingDate(nextFilters.sentDate) || nextFilters.sentDate,
      };
      const payload = await templateReportingService.getRecipients(
        nextFilters.templateId,
        scope,
        { page: nextFilters.page ?? 0, limit: nextFilters.limit ?? TEMPLATE_USERS_PAGE_SIZE },
        { status: nextFilters.status, search: nextFilters.search }
      );
      setUsersData({
        users: payload.results ?? [],
        total_count: payload.total_count ?? 0,
      });
    },
    [companyId]
  );

  const fetchUsers = useCallback(
    async (nextFilters) => {
      setIsLoading(true);
      try {
        if (USE_WHATSAPP_REPORTING) {
          await fetchReportingUsers(nextFilters);
        } else {
          await fetchLegacyUsers(nextFilters);
        }
      } catch (error) {
        showSnackBar(error?.message || 'Failed to load template users', 'error');
        setUsersData({ users: [], total_count: 0 });
      } finally {
        setIsLoading(false);
      }
    },
    [fetchLegacyUsers, fetchReportingUsers, showSnackBar]
  );

  const loadCallStatusList = useCallback(async () => {
    try {
      const res = await constActions.getMachadaloStatusList({ dashboard: 'Template Dashboard' });
      const list = res?.client_status || [];
      setCallStatusList(list);
      setGlobalCallStatusList(list);
    } catch {
      setCallStatusList([]);
    }
  }, [constActions, setGlobalCallStatusList]);

  useEffect(() => {
    const initialFilters = {
      ...DEFAULT_FILTERS,
      templateId: templateId ?? '',
      sentDate: templateSentDate ?? '',
    };
    setFilters(initialFilters);
    fetchUsers(initialFilters);
    loadCallStatusList();
  }, [templateId, templateSentDate]);

  useEffect(() => {
    const resolvedBot =
      botNumber ||
      (recoilTemplate?.name === templateName ? recoilTemplate?.bot_number : '');
    if (!templateName || !resolvedBot) {
      if (recoilTemplate?.name === templateName && recoilTemplate?.components) {
        setTemplate(recoilTemplate);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await templateService.getTemplate(templateName, resolvedBot);
        if (!cancelled && res?.status === 200 && res?.data) {
          setTemplate(normalizeTemplate(res.data));
        }
      } catch {
        if (!cancelled && recoilTemplate?.name === templateName) {
          setTemplate(recoilTemplate);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [botNumber, recoilTemplate, templateName]);

  const updateFilters = useCallback(
    (patch) => {
      setFilters((prev) => {
        const next = { ...prev, ...patch };
        fetchUsers(next);
        return next;
      });
    },
    [fetchUsers]
  );

  const onSearch = useCallback(
    (value) => {
      if (value.length > 2 || !value) {
        updateFilters({ search: value, page: 0 });
      } else {
        setFilters((prev) => ({ ...prev, search: value }));
      }
    },
    [updateFilters]
  );

  const handleChangePage = useCallback(
    (_event, newPage) => {
      updateFilters({ page: newPage });
    },
    [updateFilters]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      const limit = parseInt(event.target.value, 10) || TEMPLATE_USERS_PAGE_SIZE;
      updateFilters({ limit, page: 0 });
    },
    [updateFilters]
  );

  const handleSortingChange = useCallback(
    (sorting) => {
      if (!sorting?.length) return;
      const { id, desc } = sorting[0];
      updateFilters({
        sortField: id || 'total_users',
        sortOrder: desc ? 'desc' : 'asc',
      });
    },
    [updateFilters]
  );

  const handleSelectStatus = useCallback(
    async (option, rowIndex) => {
      const users = [...(usersData.users ?? [])];
      const row = { ...users[rowIndex] };
      row.last_call_status = option?.alais_name ?? null;

      try {
        if (USE_WHATSAPP_REPORTING) {
          await templateReportingService.updateRecipientCrm(templateId, row.gsid, {
            last_call_status: row.last_call_status,
          });
        } else {
          await templateService.updateCallStatus(row);
        }
        users[rowIndex] = row;
        setUsersData((prev) => ({ ...prev, users }));
        showSnackBar('Call status updated successfully', 'success');
      } catch (error) {
        showSnackBar(error?.message || 'Failed to update call status', 'error');
      }
    },
    [showSnackBar, templateId, usersData.users]
  );

  const onSubmitComment = useCallback(
    async (comment) => {
      if (!actionRow) return;

      try {
        if (USE_WHATSAPP_REPORTING) {
          await templateReportingService.updateRecipientCrm(templateId, actionRow.gsid, { comment });
        } else {
          const payload = {
            template_id: templateId,
            sent_date: templateSentDate,
            phone_number: actionRow.phone_number,
            gsid: actionRow.gsid,
            comment,
          };
          const res = await templateService.postUserComment(payload);
          if (!res?.status) return;
        }

        setActionRow((prev) => ({
          ...prev,
          comments: [
            ...(prev?.comments ?? []),
            { comment, comcreated_at: new Date().toISOString() },
          ],
        }));
        showSnackBar('Comment added', 'success');
      } catch (error) {
        showSnackBar(error?.message || 'Failed to add comment', 'error');
      }
    },
    [actionRow, showSnackBar, templateId, templateSentDate]
  );

  const clearFilters = useCallback(() => {
    updateFilters({
      search: '',
      status: '',
      template_status: '',
      page: 0,
    });
  }, [updateFilters]);

  return {
    filters,
    users: usersData.users ?? [],
    totalCount: usersData.total_count ?? 0,
    template,
    isLoading,
    callStatusList,
    actionRow,
    setActionRow,
    modalShowCall,
    setModalShowCall,
    onSearch,
    updateFilters,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortingChange,
    handleSelectStatus,
    onSubmitComment,
    clearFilters,
    usingReportingPipeline: USE_WHATSAPP_REPORTING,
  };
}
